import React, { useEffect, useState } from "react";
import { api, fmtCurrency } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, Receipt, FilePdf, WarningCircle, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  invoiced: "text-[#0F1E38] bg-blue-500/10 border-blue-500/30",
  paid: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  overdue: "text-red-400 bg-red-500/10 border-red-500/30",
};

const SCHEMES = [
  { key: "30-40-30", label: "30 / 40 / 30 — Kickoff · Delivery · Handover" },
  { key: "25-50-25", label: "25 / 50 / 25 — Mobilisation · Deployment · Cutover" },
  { key: "40-40-20", label: "40 / 40 / 20 — Advance · Delivery · Support" },
  { key: "100", label: "100% — Single payment on delivery" },
];

export default function Milestones() {
  const [milestones, setMilestones] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ project_id: "", scheme: "30-40-30", total_amount: "" });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [m, s, p, c] = await Promise.all([
      api.get("/milestones"),
      api.get("/milestones/summary"),
      api.get("/projects"),
      api.get("/clients"),
    ]);
    setMilestones(m.data); setSummary(s.data); setProjects(p.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.company_name]));

  const generate = async () => {
    if (!gen.project_id) { toast.error("Pick a project"); return; }
    try {
      const payload = { project_id: gen.project_id, scheme: gen.scheme };
      if (gen.total_amount) payload.total_amount = parseFloat(gen.total_amount);
      await api.post("/milestones/generate", payload);
      toast.success("Milestones generated"); setGenOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (m, status) => {
    try {
      const res = await api.put(`/milestones/${m.id}`, { status });
      toast.success(`Marked ${status}${res.data.invoice_number ? ` — ${res.data.invoice_number}` : ""}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete milestone?")) return;
    await api.delete(`/milestones/${id}`); toast.success("Deleted"); load();
  };

  const openInvoicePDF = (m) => {
    const project = projectMap[m.project_id] || {};
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked"); return; }
    const invNumber = m.invoice_number || `DRAFT-${m.id.slice(0, 6).toUpperCase()}`;
    const html = `<!doctype html><html><head><title>${invNumber}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:40px;max-width:800px;margin:auto;font-size:13px;line-height:1.5;}
        h1{font-size:32px;margin:0;letter-spacing:-0.02em;}
        .brand{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:16px;}
        .brand-r{text-align:right;font-size:11px;color:#666;}
        .meta{color:#666;font-size:12px;margin-top:8px;}
        .box{margin-top:32px;border:1px solid #eaeaea;padding:20px;}
        .box h3{margin:0 0 12px;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:#666;}
        .row{display:flex;justify-content:space-between;padding:6px 0;}
        .grand{border-top:2px solid #111;margin-top:12px;padding-top:14px;font-weight:700;font-size:22px;}
        @media print { body { padding: 20px; } }
      </style></head><body>
      <div class="brand">
        <div>
          <h1>INVOICE</h1>
          <div class="meta">
            Number: <strong>${invNumber}</strong><br/>
            Date: ${new Date().toLocaleDateString()}<br/>
            Due: ${m.due_date || "On Receipt"}<br/>
            Status: <strong style="text-transform:uppercase;">${m.status}</strong>
          </div>
        </div>
        <div class="brand-r">
          <strong style="color:#4EA1FF;font-size:16px;letter-spacing:-0.02em;">SoverenX</strong>
          <div style="font-family:monospace;letter-spacing:0.28em;color:#4EA1FF;font-size:10px;margin-top:2px;">SYSTEMS</div>
          <div style="margin-top:8px;">Network Integration Services<br/>soverenxsystems.com.au</div>
        </div>
      </div>

      <div class="box">
        <h3>Billed To</h3>
        <div><strong>${esc(clientMap[project.client] || project.client || "Client")}</strong></div>
        <div style="color:#666;">Project: ${esc(project.name || "—")}</div>
      </div>

      <div class="box">
        <h3>Milestone</h3>
        <div style="font-size:16px;margin-bottom:4px;"><strong>${esc(m.name)}</strong></div>
        <div style="color:#666;">${esc(m.description || "")}</div>

        <div class="row" style="margin-top:20px;"><span>Milestone value</span><span style="font-family:monospace;">${fmt(m.amount)}</span></div>
        ${m.percentage ? `<div class="row" style="color:#666;font-size:11px;"><span>Percentage of project</span><span style="font-family:monospace;">${m.percentage}%</span></div>` : ""}
        <div class="grand row"><span>Amount Due</span><span style="font-family:monospace;">${fmt(m.amount)}</span></div>
      </div>

      <div style="margin-top:40px;color:#888;font-size:11px;">
        <strong>Payment terms:</strong> Net 30 from invoice date. Bank transfer preferred. Please quote invoice number on payment.
      </div>

      <script>window.onload = () => { setTimeout(() => window.print(), 300); }<\/script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  const shown = filter === "all" ? milestones : milestones.filter(m => m.status === filter || (filter === "overdue" && summary?.overdue?.find(o => o.id === m.id)));

  return (
    <div>
      <PageHeader
        overline="Billing · Milestone Invoicing"
        title="Milestones & Invoices"
        subtitle="Break every project into paid milestones (30/40/30 or custom). Track pending, invoiced, and paid — with printable invoice PDFs."
        actions={
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button data-testid="gen-milestones-btn" className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Sparkle size={14} className="mr-2"/> Generate for Project</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-300 text-[#0F1E38] max-w-lg">
              <DialogHeader><DialogTitle>Generate Milestones</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Project</Label>
                  <Select value={gen.project_id} onValueChange={(v) => setGen({...gen, project_id: v})}>
                    <SelectTrigger data-testid="gen-project" className="bg-slate-50 border-slate-300 mt-1"><SelectValue placeholder="Choose a project..."/></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtCurrency(p.value)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Split Scheme</Label>
                  <Select value={gen.scheme} onValueChange={(v) => setGen({...gen, scheme: v})}>
                    <SelectTrigger data-testid="gen-scheme" className="bg-slate-50 border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SCHEMES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Override Total <span className="text-slate-500">(defaults to project value)</span></Label>
                  <Input type="number" value={gen.total_amount} onChange={(e) => setGen({...gen, total_amount: e.target.value})} className="bg-slate-50 border-slate-300 mt-1 font-mono" placeholder="Leave blank to use project value"/>
                </div>
                <div className="text-xs text-slate-500 font-mono pt-2 border-t border-slate-200">Existing <span className="text-amber-400">pending</span> milestones for this project will be replaced. Invoiced or paid milestones are preserved.</div>
              </div>
              <DialogFooter><Button data-testid="gen-confirm-btn" onClick={generate} className="bg-[#FFC107] hover:bg-[#E5A906]">Generate</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SumTile label="Pending" amount={summary.totals.pending} accent="text-amber-400" onClick={() => setFilter("pending")} active={filter==="pending"} testid="sum-pending" />
          <SumTile label="Invoiced" amount={summary.totals.invoiced} accent="text-[#0F1E38]" onClick={() => setFilter("invoiced")} active={filter==="invoiced"} testid="sum-invoiced" />
          <SumTile label="Paid" amount={summary.totals.paid} accent="text-emerald-400" onClick={() => setFilter("paid")} active={filter==="paid"} testid="sum-paid" />
          <SumTile label="Overdue" amount={summary.totals.overdue} accent="text-red-400" onClick={() => setFilter("all")} active={false} testid="sum-overdue" />
        </div>
      )}

      {/* Overdue banner */}
      {summary?.overdue?.length > 0 && (
        <div data-testid="overdue-banner" className="mb-4 border border-red-500/30 bg-red-500/[0.06] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><WarningCircle size={16} weight="fill" className="text-red-400" /><div className="overline text-red-300">{summary.overdue.length} milestone{summary.overdue.length > 1 ? "s" : ""} overdue</div></div>
          <div className="space-y-1">
            {summary.overdue.slice(0, 5).map((o) => (
              <div key={o.id} className="text-xs font-mono flex justify-between">
                <span className="text-red-300">{o.project_name} — {o.name}</span>
                <span className="text-slate-500">was due {o.due_date} · {fmtCurrency(o.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex gap-2 mb-4">
        {["all", "pending", "invoiced", "paid"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider rounded border ${filter === f ? "bg-[#FFC107]/20 border-[#FFC107] text-[#0F1E38]" : "border-slate-300 text-slate-500 hover:text-[#0F1E38]"}`}
            style={{transition: "background-color 0.15s ease, color 0.15s ease"}}
          >{f}</button>
        ))}
      </div>

      {/* Milestones table */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-200">
              <th className="px-4 py-3">Project · Milestone</th>
              <th className="text-right">Amount</th>
              <th>Due</th>
              <th>Status</th>
              <th>Invoice</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) => {
              const proj = projectMap[m.project_id] || {};
              const isOverdue = summary?.overdue?.find(o => o.id === m.id);
              return (
                <tr key={m.id} data-testid={`ms-row-${m.id}`} className="border-b border-slate-100">
                  <td className="px-4 py-3">
                    <div className="text-[#0F1E38] text-xs font-medium">{proj.name || "—"}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{m.name}</div>
                    {m.percentage > 0 && <div className="text-[10px] font-mono text-slate-500 mt-0.5">{m.percentage}% of project</div>}
                  </td>
                  <td className="text-right font-mono text-[#0F1E38]">{fmtCurrency(m.amount)}</td>
                  <td className="font-mono text-xs text-slate-500">{m.due_date || "—"}</td>
                  <td>
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${isOverdue ? STATUS_STYLE.overdue : STATUS_STYLE[m.status]}`}>{isOverdue ? "overdue" : m.status}</span>
                  </td>
                  <td className="font-mono text-[11px] text-[#0F1E38]">{m.invoice_number || "—"}</td>
                  <td className="pr-4">
                    <div className="flex gap-1.5 justify-end">
                      {m.status === "pending" && <button data-testid={`invoice-${m.id}`} onClick={() => setStatus(m, "invoiced")} className="text-[10px] font-mono uppercase px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-[#0F1E38] hover:bg-blue-500/20" style={{transition: "background-color 0.15s ease"}} title="Mark invoiced">Invoice</button>}
                      {m.status === "invoiced" && <button data-testid={`paid-${m.id}`} onClick={() => setStatus(m, "paid")} className="text-[10px] font-mono uppercase px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" style={{transition: "background-color 0.15s ease"}} title="Mark paid"><CheckCircle size={11} className="inline mr-0.5" /> Paid</button>}
                      <button data-testid={`pdf-${m.id}`} onClick={() => openInvoicePDF(m)} className="text-slate-500 hover:text-[#0F1E38] p-1" style={{transition:"color 0.15s ease"}} title="Invoice PDF"><FilePdf size={14} /></button>
                      <button data-testid={`ms-del-${m.id}`} onClick={() => del(m.id)} className="text-slate-500 hover:text-red-400 p-1" style={{transition:"color 0.15s ease"}}><Trash size={13}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 && (
          <div className="grid-bg py-16 text-center text-sm text-slate-500">
            <Receipt size={28} className="mx-auto text-slate-400 mb-2" />
            No milestones. Pick a project and click <span className="text-[#0F1E38]">Generate for Project</span>.
          </div>
        )}
      </div>
    </div>
  );
}

function SumTile({ label, amount, accent, onClick, active, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`border rounded-lg p-5 text-left tactical-card ${active ? "border-blue-500/50 bg-blue-500/[0.05]" : "border-slate-200 bg-white"}`}
    >
      <div className="overline">{label}</div>
      <div className={`mt-3 font-display font-bold text-2xl tracking-tight ${accent}`}>{fmtCurrency(amount)}</div>
    </button>
  );
}

const fmt = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function esc(s) { return (s || "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }
